import React, { useCallback, useMemo, memo, useRef, useState, useEffect } from 'react';
// Import all modes from the modes/index.js file
import { SetupMode, CustomMode, AutoMode, CompareMode } from './modes';
// Update import path to use main Services directory
import { MODES } from '../Services';
// Import context hook
import { useTp } from '../context/TpContext';
// Import all styles at once
import './styles';

// Using memo to avoid unnecessary re-renders
const Sidebar = memo(function Sidebar() {
  // Get state from context
  const { currentMode, setCurrentMode } = useTp();
  
  // Add refs and state for resizable sidebar
  const sidebarRef = useRef(null);
  const sidebarContainerRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  
  // Load saved width from localStorage on component mount
  useEffect(() => {
    const savedWidth = localStorage.getItem('tpSidebarWidth');
    if (savedWidth && sidebarRef.current && sidebarContainerRef.current) {
      sidebarRef.current.style.width = savedWidth;
      sidebarContainerRef.current.style.width = savedWidth;
    }
  }, []);
  
  // Handle sidebar resizing
  useEffect(() => {
    const sidebar = sidebarRef.current;
    const sidebarContainer = sidebarContainerRef.current;
    const resizeHandle = resizeHandleRef.current;
    
    if (!sidebar || !sidebarContainer || !resizeHandle) {
      return;
    }
    
    const updateResizeHandlePosition = () => {
      const containerWidth = sidebarContainer.getBoundingClientRect().width;
      resizeHandle.style.left = `${containerWidth}px`;
    };
    
    const handleMouseDown = (e) => {
      e.preventDefault();
      setIsResizing(true);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    };
    
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      // Calculate new width based on mouse position
      const newWidth = e.clientX;
      
      // Apply min and max constraints
      const minWidth = 300;
      const maxWidth = window.innerWidth * 0.5; // 50% of viewport
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        sidebar.style.width = `${newWidth}px`;
        sidebarContainer.style.width = `${newWidth}px`;
        
        // Update resize handle position
        resizeHandle.style.left = `${newWidth}px`;
        
        // Update the main content area dynamically (match TpLayout's with-sidebar class)
        const mainContent = document.querySelector('.tp-main.with-sidebar');
        if (mainContent) {
          mainContent.style.width = `calc(100% - ${newWidth}px)`;
        }
      }
    };
    
    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        
        // Save width to localStorage
        localStorage.setItem('tpSidebarWidth', sidebar.style.width);
      }
    };
    
    // Initial position update
    updateResizeHandlePosition();
    
    // Add event listeners
    resizeHandle.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Clean up
    return () => {
      resizeHandle.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);
  
  // Placeholder render function for components that fail to load
  const renderPlaceholder = (name) => (
    <div className="error-placeholder">
      <h3>Error: Unable to load {name} component</h3>
      <p>Please check if the component file exists and is exported correctly.</p>
    </div>
  );
  
  // Safely render a component
  const safeRender = (Component) => {
    return Component ? <Component /> : renderPlaceholder('Mode');
  };
  
  // Handle mode tab click - optimized with useCallback
  const handleModeClick = useCallback((mode) => {
    setCurrentMode(mode);
  }, [setCurrentMode]);

  return (
    <div 
      className="tp-sidebar-container" 
      ref={sidebarContainerRef}
    >
      <div 
        className="tp-sidebar" 
        ref={sidebarRef}
      >
        <div className="mode-selector">
          {MODES.map(mode => (
            <button
              key={mode}
              className={`mode-tab${currentMode === mode ? ' active' : ''}`}
              onClick={() => handleModeClick(mode)}
            >
              {mode}
            </button>
          ))}
        </div>
        
        <div className="mode-content">
          {currentMode === MODES[0] && safeRender(SetupMode)}
          {currentMode === MODES[1] && safeRender(CustomMode)}
          {currentMode === MODES[2] && safeRender(AutoMode)}
          {currentMode === MODES[3] && safeRender(CompareMode)}
        </div>
      </div>
      
      <div 
        className="tp-resize-handle" 
        ref={resizeHandleRef}
        style={{
          position: 'fixed',
          top: 0,
          left: '350px',
          width: '10px',
          height: '100vh',
          cursor: 'ew-resize',
          zIndex: 10
        }}
      ></div>
    </div>
  );
});

export default Sidebar; 